import type { SelectOption, SelectOptionGroup } from '@/shared/components/ui';
import type { UnitDropdownOption } from '@/shared/hooks/useUnitsDropdown';

/**
 * Valor do seletor de unidade dos dashboards (Analytics e Gestão Comercial).
 *
 * Um controle só responde por três escolhas: tudo (`''`), um ESTADO inteiro
 * (`state:SC`) ou uma unidade (o id). O prefixo é o que separa as duas últimas
 * — um id do Mongo nunca tem dois-pontos, então não há como colidir.
 */
const STATE_PREFIX = 'state:';

export const ALL_UNITS = '';

export const stateFilterValue = (state: string): string => `${STATE_PREFIX}${state}`;

/** O valor do seletor como parâmetros da consulta. Um ou outro, nunca os dois. */
export const unitFilterToQuery = (value: string): { unitId?: string; unitState?: string } => {
  if (!value) return {};
  if (value.startsWith(STATE_PREFIX)) return { unitState: value.slice(STATE_PREFIX.length) };
  return { unitId: value };
};

export interface UnitFilterSelect {
  options: SelectOption[];
  groups?: SelectOptionGroup[];
}

const toOption = (unit: UnitDropdownOption): SelectOption => ({ value: unit.value, label: unit.label });

/**
 * Opções do seletor, agrupadas por estado.
 *
 * O título de grupo de um `<select>` não é clicável — é limitação do
 * navegador. Por isso cada estado ganha uma primeira linha "Todas de SC", que
 * é a forma de escolher o grupo inteiro.
 *
 * Sem NENHUMA unidade com estado, devolve a lista plana de antes: um único
 * grupo "Sem estado" seria ruído para quem nunca preencheu endereço.
 */
export const buildUnitFilterSelect = (units: UnitDropdownOption[]): UnitFilterSelect => {
  const all: SelectOption = { value: ALL_UNITS, label: 'Todas as unidades' };

  const byState = new Map<string, UnitDropdownOption[]>();
  const withoutState: UnitDropdownOption[] = [];
  for (const unit of units) {
    if (!unit.state) {
      withoutState.push(unit);
      continue;
    }
    const list = byState.get(unit.state);
    if (list) list.push(unit);
    else byState.set(unit.state, [unit]);
  }

  if (byState.size === 0) return { options: [all, ...units.map(toOption)] };

  const groups: SelectOptionGroup[] = [...byState.keys()].sort().map((state) => {
    const list = byState.get(state) ?? [];
    return {
      label: state,
      options: [
        { value: stateFilterValue(state), label: `Todas de ${state} (${list.length})` },
        ...list.map(toOption),
      ],
    };
  });

  // Sem "Todas de…": unidade sem endereço não pertence a estado nenhum.
  if (withoutState.length > 0) {
    groups.push({ label: 'Sem estado', options: withoutState.map(toOption) });
  }

  return { options: [all], groups };
};
